import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { KgValue } from '@/components/efs/KgValue';
import { evidenceDownloadUrl } from '@/features/evidence/api';
import type { ApiClientError } from '@/lib/api-client';

import { fetchValuationPackages, upsertValuationPackage } from './api';
import type { ValuationPackage } from './types';

interface ValuationPackageTableProps {
  valuationId: string;
  /** Locked once this company has filed, or the valuation is confirmed. */
  readOnly: boolean;
}

/**
 * Thumbnail of one piece of inbound evidence. Photos are the common case, so
 * render an <img> first and fall back to a <video> when the bytes are not an
 * image — the same shape the inbound pages use.
 */
const EvidenceThumb = ({ fileId }: { fileId: string }) => {
  const [asVideo, setAsVideo] = useState(false);
  const src = evidenceDownloadUrl(fileId);
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="block h-12 w-16 shrink-0 overflow-hidden rounded border border-efs-border bg-efs-bg-subtle"
    >
      {asVideo ? (
        <video
          src={src}
          muted
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
        />
      ) : (
        <img
          src={src}
          alt={fileId}
          onError={() => setAsVideo(true)}
          className="h-full w-full object-cover"
        />
      )}
    </a>
  );
};

/**
 * Per-unit first valuation. The estimator prices what actually arrived, with
 * the unit's own photos and its lot's truck panorama in front of them — the
 * whole-batch single number gave them nothing to look at. Each row saves on
 * blur; the company's filed total is the sum, computed server-side.
 */
export const ValuationPackageTable = ({ valuationId, readOnly }: ValuationPackageTableProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queryKey = ['first-valuations', valuationId, 'packages'];

  const packagesQuery = useQuery<ValuationPackage[], ApiClientError>({
    queryKey,
    queryFn: () => fetchValuationPackages(valuationId),
  });
  const rows = useMemo(() => packagesQuery.data ?? [], [packagesQuery.data]);

  // Local edit buffer so typing does not fight the query cache.
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  useEffect(() => {
    setAmounts(
      Object.fromEntries(rows.map((row) => [row.inbound_package_id, row.valuation_amount ?? ''])),
    );
  }, [rows]);

  const save = useMutation<void, ApiClientError, { packageId: string; amount: string }>({
    mutationFn: ({ packageId, amount }) =>
      upsertValuationPackage(valuationId, {
        inbound_package_id: packageId,
        valuation_amount: amount,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const total = useMemo(
    () =>
      Object.values(amounts).reduce((sum, value) => {
        const n = Number(value);
        return Number.isFinite(n) ? sum + n : sum;
      }, 0),
    [amounts],
  );

  const commit = (packageId: string) => {
    const amount = amounts[packageId]?.trim();
    if (!amount || Number.isNaN(Number(amount))) return;
    if (amount === (rows.find((r) => r.inbound_package_id === packageId)?.valuation_amount ?? ''))
      return;
    save.mutate({ packageId, amount });
  };

  if (packagesQuery.isLoading) {
    return <p className="text-xs text-efs-text-muted">{t('efs.common.messages.loading')}</p>;
  }
  if (rows.length === 0) {
    return (
      <p className="rounded-md border border-efs-border bg-efs-bg-subtle px-3 py-2 text-xs text-efs-text-muted">
        {t('efs.firstValuation.packages.empty', {
          defaultValue: 'この月合計表にはまだ入荷ユニットがありません',
        })}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium">
          {t('efs.firstValuation.packages.title', { defaultValue: '入荷ユニット別 査定' })}
        </p>
        <span className="text-xs text-efs-text-muted">
          {t('efs.firstValuation.packages.total', {
            defaultValue: '合計 {{total}} 円',
            total: total.toLocaleString(),
          })}
        </span>
      </div>
      <div className="overflow-x-auto rounded-md border border-efs-border">
        <Table className="min-w-[820px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">
                {t('efs.firstValuation.packages.unit', { defaultValue: 'ユニット' })}
              </TableHead>
              <TableHead>
                {t('efs.firstValuation.packages.item', { defaultValue: '品名' })}
              </TableHead>
              <TableHead className="w-28 text-right">
                {t('efs.firstValuation.packages.kg', { defaultValue: '正味kg' })}
              </TableHead>
              <TableHead className="w-56">
                {t('efs.firstValuation.packages.photos', { defaultValue: '写真' })}
              </TableHead>
              <TableHead className="w-40 text-right">
                {t('efs.firstValuation.packages.amount', { defaultValue: '査定額（円）' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.inbound_package_id}>
                <TableCell className="font-mono text-xs">{row.package_code}</TableCell>
                <TableCell className="text-xs">{row.raw_incoming_item_name}</TableCell>
                <TableCell className="text-right text-xs">
                  {row.net_kg ? <KgValue value={row.net_kg} /> : '—'}
                </TableCell>
                <TableCell>
                  {row.photo_evidence_file_ids.length === 0 ? (
                    <span className="text-xs text-amber-900 dark:text-amber-100">
                      {t('efs.firstValuation.packages.no_photo', { defaultValue: '写真なし' })}
                    </span>
                  ) : (
                    <div className="flex gap-1 overflow-x-auto">
                      {row.photo_evidence_file_ids.slice(0, 4).map((fileId) => (
                        <EvidenceThumb key={fileId} fileId={fileId} />
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <Input
                    className="text-right font-mono tabular-nums"
                    inputMode="decimal"
                    placeholder="0"
                    disabled={readOnly}
                    value={amounts[row.inbound_package_id] ?? ''}
                    onChange={(event) =>
                      setAmounts((prev) => ({
                        ...prev,
                        [row.inbound_package_id]: event.target.value,
                      }))
                    }
                    onBlur={() => commit(row.inbound_package_id)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {save.error ? <p className="text-xs text-red-900">{save.error.message}</p> : null}
    </div>
  );
};
